import { Box, Typography, Grid, Card, CardHeader, CardContent, Divider, LinearProgress } from '@mui/material';
import {
  People,
  CalendarMonth,
  EventBusy,
  Assignment,
} from '@mui/icons-material';
import { StatsCard } from '@/components/dashboard/StatsCard';
import { SubstitutionWidget } from '@/components/dashboard/SubstitutionWidget';
import { RecentActivity } from '@/components/dashboard/RecentActivity';
import { QuickActions } from '@/components/dashboard/QuickActions';
import { useQuery } from '@tanstack/react-query';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { getDashboardStatsDirect } from '@timetable/api';
import { useAuthStore } from '@/store/authStore';

const mockActivities = [
  {
    id: '1',
    type: 'absence' as const,
    description: 'Teacher John Doe marked absent for Period 3',
    timestamp: new Date(Date.now() - 1000 * 60 * 15),
    user: 'Admin',
  },
  {
    id: '2',
    type: 'assignment' as const,
    description: 'Substitute assigned to Class V-A',
    timestamp: new Date(Date.now() - 1000 * 60 * 30),
    user: 'System',
  },
  {
    id: '3',
    type: 'update' as const,
    description: 'Timetable updated for Class III-B',
    timestamp: new Date(Date.now() - 1000 * 60 * 60),
    user: 'Admin',
  },
  {
    id: '4',
    type: 'create' as const,
    description: 'New teacher added: Jane Smith',
    timestamp: new Date(Date.now() - 1000 * 60 * 120),
    user: 'Admin',
  },
];

export function DashboardPage() {
  const session = useAuthStore((state) => state.session);
  const schoolContext = useAuthStore((state) => state.schoolContext);
  const user = useAuthStore((state) => state.user);
  const isSuperAdmin = user?.role === 'super_admin';
  
  // Check if user should see substitution widget (Admin and Standard roles only - Req 23.4)
  const canViewSubstitutions = user?.role === 'admin' || user?.role === 'standard' || user?.role === 'super_admin';

  // For Super Admin: schoolContext can be null (All Schools) or a specific school_id
  // For other users: use their assigned school_id
  const effectiveSchoolId = isSuperAdmin ? schoolContext : user?.school_id;

  // Fetch real dashboard statistics with school_id filtering
  const { data: stats, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['dashboard', 'stats', effectiveSchoolId ?? 'all-schools'],
    queryFn: async () => {
      if (!session?.access_token) {
        return {
          totalTeachers: 0,
          totalClasses: 0,
          pendingAbsences: 0,
          activeAssignments: 0,
        };
      }

      try {
        // If effectiveSchoolId is null (Super Admin viewing All Schools), 
        // the API should return aggregated stats
        const result = await getDashboardStatsDirect(
          session.access_token, 
          effectiveSchoolId ?? ''
        );
        return result;
      } catch (error) {
        console.error('Dashboard stats error:', error);
        return {
          totalTeachers: 0,
          totalClasses: 0,
          pendingAbsences: 0,
          activeAssignments: 0,
        };
      }
    },
    retry: 1,
    enabled: !!session?.access_token,
    refetchOnMount: true,
    staleTime: 0,
  });

  const { data: activities, isLoading: activitiesLoading } = useQuery({
    queryKey: ['dashboard', 'activities'],
    queryFn: async () => {
      // TODO: Fetch real activity data from database
      await new Promise((resolve) => setTimeout(resolve, 500));
      return mockActivities;
    },
  });

  if (statsLoading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  if (statsError) {
    console.error('Stats error:', statsError);
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Welcome to the School Timetable Management System
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Teachers"
            value={stats?.totalTeachers || 0}
            icon={<People />}
            color="primary.main"
            subtitle="Active teachers"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Classes"
            value={stats?.totalClasses || 0}
            icon={<CalendarMonth />}
            color="secondary.main"
            subtitle="Class sections"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Pending Absences"
            value={stats?.pendingAbsences || 0}
            icon={<EventBusy />}
            color="warning.main"
            subtitle="Requires attention"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Active Assignments"
            value={stats?.activeAssignments || 0}
            icon={<Assignment />}
            color="success.main"
            subtitle="Substitute assignments"
          />
        </Grid>
        
        {/* Substitution Widget - visible to Admin and Standard roles only (Req 23.4) */}
        {canViewSubstitutions && (
          <Grid item xs={12} md={6} lg={4}>
            <SubstitutionWidget />
          </Grid>
        )}
      </Grid>

      {/* Quick Actions */}
      <Box sx={{ mb: 3 }}>
        <QuickActions />
      </Box>

      {/* Recent Activity */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <RecentActivity
            activities={activities || []}
            loading={activitiesLoading}
          />
        </Grid>
        <Grid item xs={12} lg={5}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardHeader title="This week" />
                <CardContent sx={{ pt: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    Mar 24 - 28, 2026
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1 }}>
                    7
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Periods today
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Dept. breakdown" />
                <CardContent sx={{ pt: 0, display: 'grid', gap: 1.5 }}>
                  {[
                    { label: 'Mathematics', value: 14, color: 'primary' },
                    { label: 'Sciences', value: 12, color: 'success' },
                    { label: 'Languages', value: 10, color: 'warning' },
                    { label: 'Humanities', value: 9, color: 'error' },
                  ].map((item) => (
                    <Box key={item.label}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{item.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.value}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(item.value / 14) * 100}
                        color={item.color as 'primary'}
                        sx={{ height: 7 }}
                      />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
